import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Badge, Button, Card } from '@/ui/primitives'
import { Camp } from '@/ui/form'
import { PartenerPicker } from '@/ui/partner-picker'
import { MONEDE, TIPURI_SERVICIU } from '@/lib/domeniu'
import { lei } from '@/lib/money'
import {
  CAMERE, MESE, REGIUNI, type Camera, type GrupFacturare, type ServiciuExtra,
  type StareWizard, type Turist, calculFiscal, genereazaCamere, genereazaGrupuri,
  nopti, nr, persoane, turistiiTuturorCamerelor,
} from './stare'

export type TipTva = { code: string; label: string; percent: number; is_margin: boolean; for_regions: string }

export const useTipuriTva = () => useQuery({
  queryKey: ['tipuri-tva'],
  queryFn: () => api.get<{ vat_types: TipTva[] }>('/vat-settings/'),
  staleTime: 30 * 60_000,
})

type Props = {
  s: StareWizard
  set: (patch: Partial<StareWizard>) => void
  tva: TipTva[]
  curs: number
}

const CAMP = 'h-9 w-full px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[14px]'

/* ─────────────────────────────── PASUL 1 ─────────────────────────────── */

export function Pas1({ s, set, tva, curs }: Props) {
  const tipTva = tva.find((t) => t.code === s.vat_type)
  const c = calculFiscal(s, tipTva?.percent ?? 0, tipTva?.is_margin ?? false)
  const cursFolosit = nr(s.exchange_rate) || curs
  const inLei = (v: number) => (s.currency === 'RON' ? v : v * cursFolosit)

  return (
    <div className="space-y-6">
      <Sectiune titlu="Serviciul principal" descriere="Ce vinde agentia.">
        <Camp eticheta="Tip serviciu" tip="select" valoare={s.service_type}
          seteaza={(v) => set({ service_type: v })} optiuni={TIPURI_SERVICIU} />
        <Camp eticheta="Destinatie" valoare={s.destination} seteaza={(v) => set({ destination: v })}
          obligatoriu />
        <Camp eticheta="Hotel" valoare={s.hotel} seteaza={(v) => set({ hotel: v })} latime="plin" />
        <PartenerPicker eticheta="Furnizor" valoare={s.supplier_name} pentruRezervare={false}
          seteaza={(v) => set({ supplier_name: v })}
          alege={(p) => set({ supplier_name: p.name, supplier_id: p.id })} />
        <Camp eticheta="Referinta furnizor" valoare={s.supplier_ref}
          seteaza={(v) => set({ supplier_ref: v })} />
      </Sectiune>

      <Sectiune titlu="Perioada si persoanele"
        descriere={`${nopti(s)} nopti · ${persoane(s)} persoane`}>
        <Camp eticheta="Check-in" tip="date" valoare={s.check_in} seteaza={(v) => set({ check_in: v })}
          obligatoriu />
        <Camp eticheta="Check-out" tip="date" valoare={s.check_out} seteaza={(v) => set({ check_out: v })}
          obligatoriu />
        <Camp eticheta="Adulti" tip="number" valoare={s.adults} seteaza={(v) => set({ adults: v })} />
        <Camp eticheta="Copii" tip="number" valoare={s.children} seteaza={(v) => set({ children: v })} />
        <Camp eticheta="Infanti" tip="number" valoare={s.infants} seteaza={(v) => set({ infants: v })} />
        <Camp eticheta="Camere" tip="number" valoare={s.num_rooms} seteaza={(v) => set({ num_rooms: v })} />
      </Sectiune>

      <Sectiune titlu="Bani si regim fiscal"
        descriere="Cursul gol = cel al zilei (BNR + 2%).">
        <Camp eticheta="Cost furnizor" valoare={s.cost_price} seteaza={(v) => set({ cost_price: v })} />
        <Camp eticheta="Pret de vanzare" valoare={s.sale_price} seteaza={(v) => set({ sale_price: v })}
          obligatoriu />
        <Camp eticheta="Moneda" tip="select" valoare={s.currency} seteaza={(v) => set({ currency: v })}
          optiuni={MONEDE} />
        <Camp eticheta="Curs" valoare={s.exchange_rate} seteaza={(v) => set({ exchange_rate: v })}
          indiciu={`azi: ${curs.toFixed(4)}`} />
        <Camp eticheta="Regiunea destinatiei" tip="select" valoare={s.destination_region}
          seteaza={(v) => {
            const r = REGIUNI.find((x) => x.v === v)
            set({ destination_region: v, vat_type: r?.tvaImplicit ?? s.vat_type })
          }}
          optiuni={REGIUNI.map((r) => ({ v: r.v, t: r.t }))} />
        <Camp eticheta="Tip TVA" tip="select" valoare={s.vat_type} seteaza={(v) => set({ vat_type: v })}
          optiuni={tva.map((t) => ({ v: t.code, t: t.label }))} />
      </Sectiune>

      <Card className="p-5">
        <p className="text-[12.5px] text-muted mb-3">
          Calculul facturarii {tipTva ? `· ${tipTva.code}, ${tipTva.percent}%${tipTva.is_margin ? ', regim marja' : ''}` : ''}
        </p>
        <div className="grid gap-4 sm:grid-cols-4">
          <Cifra eticheta="Cost" v={c.cost} m={s.currency} lei={inLei(c.cost)} />
          <Cifra eticheta="Vanzare" v={c.vanzare} m={s.currency} lei={inLei(c.vanzare)} tare />
          <Cifra eticheta={`Marja (${c.procentMarja.toFixed(1)}%)`} v={c.marja} m={s.currency}
            lei={inLei(c.marja)} ton={c.marja < 0 ? 'rau' : 'bun'} />
          <Cifra eticheta="TVA" v={c.tva} m={s.currency} lei={inLei(c.tva)} />
        </div>
        <p className="mt-3 text-[12px] text-muted">
          {tipTva?.is_margin
            ? `Regim marja: TVA = marja − marja / ${(1 + (tipTva.percent / 100)).toFixed(2)}.`
            : `Regim normal: TVA = pret de vanzare × ${tipTva?.percent ?? 0}%.`}
          {' '}Conversia foloseste cursul {cursFolosit.toFixed(4)} lei/EUR.
        </p>
      </Card>
    </div>
  )
}

/* ─────────────────────────────── PASUL 2 ─────────────────────────────── */

export function Pas2({ s, set }: Props) {
  const schimbaCamera = (i: number, patch: Partial<Camera>) =>
    set({ rooms: s.rooms.map((c, j) => (j === i ? { ...c, ...patch } : c)) })
  const schimbaTurist = (ci: number, ti: number, patch: Partial<Turist>) =>
    schimbaCamera(ci, {
      tourists: s.rooms[ci]!.tourists.map((t, j) => (j === ti ? { ...t, ...patch } : t)),
    })

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => set({ rooms: genereazaCamere(s) })}>
          {s.rooms.length ? 'Regenereaza camerele din pasul 1' : 'Genereaza camerele'}
        </Button>
        <Button onClick={() => set({ rooms: [...s.rooms, { room_type: 'DBL', meal_type: 'BB', tourists: [{ full_name: '', is_titular: true }] }] })}>
          Adauga o camera
        </Button>
        <span className="text-[13px] text-muted">
          {s.rooms.length} camere · {turistiiTuturorCamerelor(s).length} turisti
          {persoane(s) !== turistiiTuturorCamerelor(s).length &&
            <span className="text-warn"> (pasul 1 spune {persoane(s)})</span>}
        </span>
      </div>

      {s.rooms.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-[14px] text-muted">
            Nicio camera. Apasa „Genereaza camerele" — se impart persoanele din pasul 1.
          </p>
        </Card>
      )}

      {s.rooms.map((c, ci) => (
        <Card key={ci} className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <p className="text-[14.5px] font-medium mr-auto">Camera {ci + 1}</p>
            <label className="text-[12.5px] text-muted">
              Tip camera
              <select value={c.room_type} onChange={(e) => schimbaCamera(ci, { room_type: e.target.value })}
                className={CAMP + ' mt-1 w-40'}>
                {CAMERE.map((x) => <option key={x.v} value={x.v}>{x.t}</option>)}
              </select>
            </label>
            <label className="text-[12.5px] text-muted">
              Masa
              <select value={c.meal_type} onChange={(e) => schimbaCamera(ci, { meal_type: e.target.value })}
                className={CAMP + ' mt-1 w-48'}>
                {MESE.map((x) => <option key={x.v} value={x.v}>{x.t}</option>)}
              </select>
            </label>
            <button onClick={() => set({ rooms: s.rooms.filter((_, j) => j !== ci) })}
              className="text-[13px] text-muted hover:text-bad h-9">sterge camera</button>
          </div>

          <div className="space-y-2">
            {c.tourists.map((t, ti) => (
              <div key={ti} className="grid gap-2 sm:grid-cols-[1fr_140px_150px_110px_auto] items-center">
                <input value={t.full_name} placeholder={`Nume turist ${ti + 1}`}
                  onChange={(e) => schimbaTurist(ci, ti, { full_name: e.target.value })} className={CAMP} />
                <input type="date" value={t.birth_date ?? ''} title="Data nasterii"
                  onChange={(e) => schimbaTurist(ci, ti, { birth_date: e.target.value })} className={CAMP} />
                <input value={t.cnp ?? ''} placeholder="CNP"
                  onChange={(e) => schimbaTurist(ci, ti, { cnp: e.target.value })} className={CAMP} />
                <label className="flex items-center gap-1.5 text-[12.5px] text-muted">
                  <input type="checkbox" checked={Boolean(t.is_titular)}
                    onChange={(e) => schimbaCamera(ci, {
                      tourists: c.tourists.map((x, j) => ({ ...x, is_titular: j === ti ? e.target.checked : false })),
                    })} />
                  titular
                </label>
                <button onClick={() => schimbaCamera(ci, { tourists: c.tourists.filter((_, j) => j !== ti) })}
                  className="text-[13px] text-muted hover:text-bad">scoate</button>
              </div>
            ))}
            <Button onClick={() => schimbaCamera(ci, { tourists: [...c.tourists, { full_name: '' }] })}>
              Adauga turist
            </Button>
          </div>
        </Card>
      ))}
    </div>
  )
}

/* ─────────────────────────────── PASUL 3 ─────────────────────────────── */

export function Pas3({ s, set, curs }: Props) {
  const schimba = (i: number, patch: Partial<ServiciuExtra>) =>
    set({ extra_services: s.extra_services.map((x, j) => (j === i ? { ...x, ...patch } : x)) })
  const total = s.extra_services.reduce((t, x) => t + nr(x.sale_price), 0)
  const cursFolosit = nr(s.exchange_rate) || curs

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button onClick={() => set({
          extra_services: [...s.extra_services, {
            service_type: 'TRANSFER', description: '', sale_price: '', cost_price: '',
            currency: s.currency, begin_date: s.check_in,
          }],
        })}>Adauga serviciu</Button>
        {s.extra_services.length > 0 && (
          <span className="text-[13px] text-muted tnum">
            {s.extra_services.length} servicii · {total.toFixed(2)} {s.currency}
            {s.currency !== 'RON' && ` (${lei(total * cursFolosit)} lei)`}
          </span>
        )}
      </div>

      {s.extra_services.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-[14px] text-muted">
            Fara servicii suplimentare. Aici se adauga transferuri, asigurari, excursii.
          </p>
        </Card>
      )}

      {s.extra_services.map((x, i) => (
        <Card key={i} className="p-5">
          <div className="grid gap-3 sm:grid-cols-[160px_1fr_130px_130px_110px_auto] items-end">
            <label className="text-[12.5px] text-muted">Tip
              <select value={x.service_type} onChange={(e) => schimba(i, { service_type: e.target.value })}
                className={CAMP + ' mt-1'}>
                {TIPURI_SERVICIU.map((t) => <option key={t.v} value={t.v}>{t.t}</option>)}
              </select>
            </label>
            <label className="text-[12.5px] text-muted">Descriere
              <input value={x.description} onChange={(e) => schimba(i, { description: e.target.value })}
                className={CAMP + ' mt-1'} placeholder="ex.: Transfer aeroport – hotel, dus-intors" />
            </label>
            <label className="text-[12.5px] text-muted">Vanzare
              <input value={x.sale_price} onChange={(e) => schimba(i, { sale_price: e.target.value })}
                className={CAMP + ' mt-1'} />
            </label>
            <label className="text-[12.5px] text-muted">Cost
              <input value={x.cost_price} onChange={(e) => schimba(i, { cost_price: e.target.value })}
                className={CAMP + ' mt-1'} />
            </label>
            <label className="text-[12.5px] text-muted">Moneda
              <select value={x.currency} onChange={(e) => schimba(i, { currency: e.target.value })}
                className={CAMP + ' mt-1'}>
                {MONEDE.map((m) => <option key={m.v} value={m.v}>{m.v}</option>)}
              </select>
            </label>
            <button onClick={() => set({ extra_services: s.extra_services.filter((_, j) => j !== i) })}
              className="text-[13px] text-muted hover:text-bad h-9">sterge</button>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 mt-3">
            <Camp eticheta="Furnizor" valoare={x.supplier_name ?? ''}
              seteaza={(v) => schimba(i, { supplier_name: v })} />
            <Camp eticheta="Data" tip="date" valoare={x.begin_date ?? ''}
              seteaza={(v) => schimba(i, { begin_date: v })} />
            <Camp eticheta="Referinta" valoare={x.supplier_ref ?? ''}
              seteaza={(v) => schimba(i, { supplier_ref: v })} />
          </div>
        </Card>
      ))}
    </div>
  )
}

/* ─────────────────────────────── PASUL 4 ─────────────────────────────── */

export function Pas4({ s, set, tva, curs }: Props) {
  const tipTva = tva.find((t) => t.code === s.vat_type)
  const c = calculFiscal(s, tipTva?.percent ?? 0, tipTva?.is_margin ?? false)
  const turisti = turistiiTuturorCamerelor(s)
  const totalGrupuri = s.billing_groups.reduce((t, g) => t + nr(g.invoice_amount), 0)
  const diferenta = c.vanzare - totalGrupuri
  const cursFolosit = nr(s.exchange_rate) || curs

  const schimbaGrup = (i: number, patch: Partial<GrupFacturare>) =>
    set({ billing_groups: s.billing_groups.map((g, j) => (j === i ? { ...g, ...patch } : g)) })

  return (
    <div className="space-y-6">
      <Sectiune titlu="Titular" descriere="Cine semneaza si cine plateste.">
        <Camp eticheta="Tip" tip="select" valoare={s.titular_type}
          seteaza={(v) => set({ titular_type: v as 'PF' | 'PJ' })}
          optiuni={[{ v: 'PF', t: 'Persoana fizica' }, { v: 'PJ', t: 'Persoana juridica' }]} />
        <Camp eticheta="Nume" valoare={s.titular_name} seteaza={(v) => set({ titular_name: v })}
          obligatoriu />
        {s.titular_type === 'PJ'
          ? <Camp eticheta="CUI" valoare={s.titular_cui} seteaza={(v) => set({ titular_cui: v })} obligatoriu />
          : <Camp eticheta="CNP" valoare={s.titular_cnp} seteaza={(v) => set({ titular_cnp: v })} />}
        <Camp eticheta="Telefon" tip="tel" valoare={s.titular_phone} seteaza={(v) => set({ titular_phone: v })} />
        <Camp eticheta="Email" tip="email" valoare={s.titular_email} seteaza={(v) => set({ titular_email: v })} />
        <Camp eticheta="Adresa" valoare={s.titular_address} seteaza={(v) => set({ titular_address: v })}
          latime="plin" />
      </Sectiune>

      {turisti.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] text-muted">Ia titularul dintre turisti:</span>
          {turisti.filter((t) => t.full_name.trim()).map((t, i) => (
            <button key={i}
              onClick={() => set({ titular_name: t.full_name, titular_cnp: t.cnp ?? s.titular_cnp })}
              className="h-8 px-3 rounded-full border border-line text-[13px] hover:border-accent">
              {t.full_name}
            </button>
          ))}
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h3 className="text-[14.5px] font-medium mr-auto">Grupuri de facturare</h3>
          <Button onClick={() => set({ billing_groups: genereazaGrupuri(s, c.vanzare) })}>
            Genereaza cate unul pe camera
          </Button>
          <Button onClick={() => set({
            billing_groups: [{
              name: 'Factura unica', partner_name: s.titular_name,
              partner_vat_no: s.titular_cnp || s.titular_cui || null,
              invoice_amount: c.vanzare.toFixed(2), currency: s.currency,
              room_indices: s.rooms.map((_, i) => i),
            }],
          })}>O singura factura, pe titular</Button>
        </div>

        {s.billing_groups.length === 0 ? (
          <Card className="p-6 text-center">
            <p className="text-[14px] text-muted">
              Fara grupuri, se emite o singura factura pe titular, cu totalul rezervarii.
            </p>
          </Card>
        ) : (
          <Card className="p-5 space-y-3">
            {s.billing_groups.map((g, i) => (
              <div key={i} className="grid gap-3 sm:grid-cols-[1fr_1fr_140px_100px_auto] items-end">
                <Camp eticheta="Grup" valoare={g.name} seteaza={(v) => schimbaGrup(i, { name: v })} />
                <Camp eticheta="Facturat catre" valoare={g.partner_name}
                  seteaza={(v) => schimbaGrup(i, { partner_name: v })} />
                <Camp eticheta="Suma" valoare={g.invoice_amount}
                  seteaza={(v) => schimbaGrup(i, { invoice_amount: v })} />
                <Camp eticheta="CNP / CUI" valoare={g.partner_vat_no ?? ''}
                  seteaza={(v) => schimbaGrup(i, { partner_vat_no: v })} />
                <button onClick={() => set({ billing_groups: s.billing_groups.filter((_, j) => j !== i) })}
                  className="text-[13px] text-muted hover:text-bad h-9">sterge</button>
              </div>
            ))}
            <div className="pt-3 border-t border-line flex flex-wrap items-center gap-4 text-[13.5px]">
              <span className="tnum">
                Grupuri: {totalGrupuri.toFixed(2)} {s.currency}
                {s.currency !== 'RON' && <span className="text-muted"> ({lei(totalGrupuri * cursFolosit)} lei)</span>}
              </span>
              <span className="tnum text-muted">Servicii: {c.vanzare.toFixed(2)} {s.currency}</span>
              {Math.abs(diferenta) > 0.01
                ? <Badge tone="bad">difera cu {diferenta.toFixed(2)} {s.currency}</Badge>
                : <Badge tone="good">se potrivesc</Badge>}
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────────── PASUL 5 ─────────────────────────────── */

export function Pas5({ s, tva, curs }: Props) {
  const tipTva = tva.find((t) => t.code === s.vat_type)
  const c = calculFiscal(s, tipTva?.percent ?? 0, tipTva?.is_margin ?? false)
  const cursFolosit = nr(s.exchange_rate) || curs
  const inLei = (v: number) => (s.currency === 'RON' ? v : v * cursFolosit)
  const turisti = turistiiTuturorCamerelor(s)

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <p className="text-[12.5px] text-muted">Rezervarea</p>
            <p className="text-[15px] font-medium">{s.destination || '—'}{s.hotel ? ` · ${s.hotel}` : ''}</p>
            <p className="text-[13.5px] text-muted">
              {s.check_in} – {s.check_out} · {nopti(s)} nopti · {persoane(s)} persoane
            </p>
            <p className="text-[13.5px] text-muted">
              {s.rooms.length} camere · {turisti.length} turisti completati
            </p>
            {s.supplier_name && <p className="text-[13px] text-muted">furnizor: {s.supplier_name}</p>}
          </div>
          <div className="space-y-1.5">
            <p className="text-[12.5px] text-muted">Titular</p>
            <p className="text-[15px] font-medium">{s.titular_name || '—'}</p>
            <p className="text-[13.5px] text-muted">
              {s.titular_type === 'PJ' ? `CUI ${s.titular_cui || '—'}` : `CNP ${s.titular_cnp || '—'}`}
            </p>
            <p className="text-[13px] text-muted">{[s.titular_phone, s.titular_email].filter(Boolean).join(' · ')}</p>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <p className="text-[12.5px] text-muted mb-3">
          Bani {tipTva ? `· ${tipTva.code}` : ''} · curs {cursFolosit.toFixed(4)}
        </p>
        <div className="grid gap-4 sm:grid-cols-4">
          <Cifra eticheta="Cost" v={c.cost} m={s.currency} lei={inLei(c.cost)} />
          <Cifra eticheta="Vanzare" v={c.vanzare} m={s.currency} lei={inLei(c.vanzare)} tare />
          <Cifra eticheta={`Marja (${c.procentMarja.toFixed(1)}%)`} v={c.marja} m={s.currency}
            lei={inLei(c.marja)} ton={c.marja < 0 ? 'rau' : 'bun'} />
          <Cifra eticheta="TVA" v={c.tva} m={s.currency} lei={inLei(c.tva)} />
        </div>
      </Card>

      {s.billing_groups.length > 0 && (
        <Card className="p-5">
          <p className="text-[12.5px] text-muted mb-2">
            Se emit {s.billing_groups.length} facturi
          </p>
          <ul className="space-y-1 text-[13.5px]">
            {s.billing_groups.map((g, i) => (
              <li key={i} className="flex justify-between">
                <span>{g.partner_name || g.name}</span>
                <span className="tnum">{nr(g.invoice_amount).toFixed(2)} {g.currency}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}

/* ─────────────────────────────── PASUL 6 ─────────────────────────────── */

export function Pas6({ s, set }: Props) {
  return (
    <div className="space-y-5">
      <Sectiune titlu="Ultimele detalii" descriere="Ce se scrie pe rezervare.">
        <Camp eticheta="Observatii" tip="textarea" valoare={s.remarks}
          seteaza={(v) => set({ remarks: v })} latime="plin" />
      </Sectiune>
      <Card className="p-5">
        <label className="flex items-center gap-2.5 text-[14px]">
          <input type="checkbox" checked={s.generate_bon_comanda}
            onChange={(e) => set({ generate_bon_comanda: e.target.checked })} />
          Genereaza bonul de comanda
        </label>
        <p className="mt-2 text-[12.5px] text-muted">
          Rezervarea se creeaza in starea „Optiune". Facturile pentru grupurile de facturare
          se emit automat de backend, la finalizare.
        </p>
      </Card>
    </div>
  )
}

/* ─────────────────────────────── bucati comune ─────────────────────────────── */

function Sectiune({ titlu, descriere, children }: { titlu: string; descriere?: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-5 sm:grid-cols-[200px_1fr]">
      <div>
        <h3 className="text-[14.5px] font-medium">{titlu}</h3>
        {descriere && <p className="mt-1 text-[12.5px] text-muted">{descriere}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">{children}</div>
    </section>
  )
}

function Cifra({ eticheta, v, m, lei: inLei, tare, ton }: {
  eticheta: string; v: number; m: string; lei: number; tare?: boolean; ton?: 'bun' | 'rau'
}) {
  const f = (n: number) => new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
  return (
    <div>
      <p className="text-[12px] text-muted">{eticheta}</p>
      <p className={'tnum text-[17px] leading-tight ' +
        (ton === 'rau' ? 'text-bad ' : ton === 'bun' ? 'text-good ' : '') + (tare ? 'font-medium' : '')}>
        {f(v)} <span className="text-[11.5px] text-muted">{m}</span>
      </p>
      {m !== 'RON' && <p className="tnum text-[12px] text-muted">{f(inLei)} lei</p>}
    </div>
  )
}
